import { h, setChildren } from "../core/dom.js";
import { icon } from "../lib/icons.js";
import { fmtDuration } from "../lib/format.js";
import { restAlertsEnabled } from "../lib/notify.js";
import * as local from "../lib/nativeLocal.js";
import * as display from "../lib/nativeScreen.js"; // not `screen` — that shadows window.screen
import { recoveryMessage } from "../lib/restHr.js";

let active = null; // singleton

export function attachRest(container = document.body) {
  if(active && active.el.parentElement !== container)container.append(active.el);
}

const AOD_IDLE_MS = 8000; // hands-off this long during a rest → drop to the always-on face
const AOD_BRIGHTNESS = 0.08;
const AOD_DRIFT_MS = 45000;

export function startRest(seconds = 90, options = {}) {
  return startRestAt(Date.now() + Math.max(1, Math.round(seconds)) * 1000, options);
}

/**
 * Reconcile the singleton timer to a server-owned absolute deadline. Repeated polling with the
 * same deadline is a no-op; an adjustment on another device updates the existing overlay/alarm
 * instead of tearing it down and creating a second one.
 */
export function syncRest(endsAt, options = {}) {
  const endMs = endsAt instanceof Date ? endsAt.getTime() : Date.parse(endsAt);
  if (!Number.isFinite(endMs) || endMs <= Date.now()) {
    stopRest();
    return null;
  }
  if (active) {
    active.update(endMs, options);
    return active;
  }
  return startRestAt(endMs, options);
}

function startRestAt(initialEndAt, initialOptions = {}) {
  stopRest();
  let options = initialOptions;
  let total = Math.max(1, Math.ceil((initialEndAt - Date.now()) / 1000));
  // Wall-clock deadline, never a decrementing counter: a hidden WebView (Android app in the
  // background, phone in your pocket) has setInterval throttled to about once a minute, so a
  // per-tick `remaining--` visibly stops counting and finishes minutes late. Deriving what's
  // left from Date.now() means the display is right the instant you look at it again.
  let endAt = initialEndAt;
  let finished = false;
  const left = () => Math.ceil((endAt - Date.now()) / 1000);

  let aod = null; // the always-on face, while it's showing
  let aodIdle = 0; // pending "you've stopped touching the phone" timeout
  let aodDismissed = false; // tapped out of it — don't creep back during this rest

  const timeEl = h("span", { class: "rt-time" }, fmtDuration(total));
  const bar = h("i", { class: "rt-bar", style: { width: "100%" } });
  // Filled by refreshRecovery() when the band says you're back down. Never touches the
  // countdown — see lib/restHr.js for why this can only ever be a suggestion.
  const hrEl = h("div", { class: "rt-hr" });
  // Normally just "Rest". When you're training with someone and the clock was started by THEIR
  // set, say so: a countdown you didn't start is otherwise unaccountable.
  const nextEl = h("div",{class:"rt-next"});
  const labelEl = h("div", { class: "rt-label" }, "Rest");
  const el = h(
    "div",
    { class: "rest-timer" },
    icon("timer", { size: 26, className: "icon accent" }),
    timeEl,
    h("div", { class: "grow" }, labelEl, nextEl, hrEl),
    h("button", { class: "icon-btn ghost", "aria-label":"Shorten rest by 15 seconds", onClick: () => adjust(-15) }, "−15"),
    h("button", { class: "icon-btn ghost", "aria-label":"Extend rest by 15 seconds", onClick: () => adjust(15) }, "+15"),
    h("button", {
      class: "btn btn-ghost",
      style: { padding: "8px 14px" },
      onClick: () => {
        options.onSkip?.();
        stopRest();
      },
    }, "Skip rest"),
    bar
  );
  (options.container?.() || document.body).appendChild(el);

  function paint() {
    const r = Math.max(0, left());
    const frac = Math.min(1, Math.max(0, r / total));
    timeEl.textContent = fmtDuration(r);
    bar.style.width = `${frac * 100}%`;
    refreshNext();
    if (!aod) return;
    aod.time.textContent = fmtDuration(r);
    aod.ring.setAttribute("stroke-dashoffset", String(aod.circumference * (1 - frac)));
  }

  // The heart-rate suggestion, in both places the rest is shown. Cached on the last rendered
  // string so a twice-a-second tick doesn't rebuild text nodes under the user's finger.
  let lastRecovery = null;
  function refreshRecovery() {
    let msg = null;
    try { msg = recoveryMessage(options.recovery?.()); } catch { msg = null; }
    if (msg === lastRecovery && (!aod || aod.hr.textContent === (msg || ""))) return;
    lastRecovery = msg;
    hrEl.textContent = msg || "";
    if (aod) aod.hr.textContent = msg || "";
  }

  // Who started this rest, if it wasn't you. Mutated in place rather than rebuilt — the bar's
  // buttons sit beside it and are being tapped.
  let lastStartedBy;
  function refreshLabel() {
    let who = null;
    try { who = options.startedBy?.() || null; } catch { who = null; }
    if (who === lastStartedBy) return;
    lastStartedBy = who;
    labelEl.textContent = who ? `${who} finished a set` : "Rest";
    if (aod) aod.label.textContent = who ? `${who}'s set` : "Rest";
  }

  function tick() {
    paint();
    refreshRecovery();
    refreshLabel();
    if (left() > 0) return;
    // Hidden and the OS is holding the alarm: it already buzzed (or is about to) at the right
    // moment — don't also beep out of a pocket a throttled minute later.
    done({ silent: document.visibilityState === "hidden" && local.available() });
  }

  function adjust(delta) {
    endAt = Math.max(Date.now() + 1000, endAt + delta * 1000);
    total = Math.max(1, Math.ceil((endAt - Date.now()) / 1000));
    syncAlarm();
    paint();
    options.onDeadlineChange?.(new Date(endAt).toISOString());
  }

  // ── Always-on display ──────────────────────────────────────────────────────────────────────
  // A rest timer you have to wake the phone to read isn't much of a timer. The screen is held on
  // for the whole rest; leave the phone alone for a few seconds and it drops to a near-black face
  // with the countdown on it — cheap to keep lit on OLED, where an unlit pixel draws no power.
  //
  // Native shell only (`display.available()`): elsewhere nothing can dim the backlight or stop the
  // display sleeping on schedule, so a full-screen black overlay would just hide the app.
  function armAod() {
    clearTimeout(aodIdle);
    if (aod || aodDismissed || finished || !display.available()) return;
    aodIdle = setTimeout(enterAod, AOD_IDLE_MS);
  }

  function enterAod() {
    if (aod || finished || document.visibilityState !== "visible") return;
    aod = buildAod();
    document.body.appendChild(aod.el);
    void display.setAod(true, AOD_BRIGHTNESS);
    refreshNext();
    refreshLabel();
    paint();
  }

  function exitAod({ dismissed = true } = {}) {
    clearTimeout(aodIdle);
    if (!aod) return;
    clearInterval(aod.drift);
    aod.el.remove();
    aod = null;
    aodDismissed = dismissed;
    void display.setAod(false);
  }

  function buildAod() {
    const circumference = 2 * Math.PI * 104;
    const ring = h("circle", {
      class: "aod-ring", cx: 120, cy: 120, r: 104,
      "stroke-dasharray": circumference, "stroke-dashoffset": 0,
    });
    const time = h("div", { class: "aod-time" }, fmtDuration(Math.max(0, left())));
    const next = h("div", { class: "aod-next" }); // filled by refreshNext(); `:empty` hides it
    const hr = h("div", { class: "aod-hr" }, lastRecovery || "");
    const label = h("div", { class: "aod-label" }, lastStartedBy ? `${lastStartedBy}'s set` : "Rest");
    const inner = h("div", { class: "aod-inner" },
      h("div", { class: "aod-dial-wrap" },
        h("svg", { class: "aod-dial", viewBox: "0 0 240 240" },
          h("circle", { class: "aod-track", cx: 120, cy: 120, r: 104 }),
          ring),
        h("div", { class: "aod-face" }, time, label)),
      next, hr);
    const el = h("div", { class: "rest-aod", onPointerDown: () => exitAod() },
      inner,
      h("div", { class: "aod-hint" }, "tap to wake"));
    // Burn-in insurance: nudge the whole face every so often. The same bright ring held for
    // minutes, workout after workout, is exactly what marks an OLED panel.
    const drift = setInterval(() => {
      const nudge = () => (Math.random() * 24 - 12).toFixed(1);
      inner.style.transform = `translate(${nudge()}px, ${nudge()}px)`;
    }, AOD_DRIFT_MS);
    return { el, time, ring, circumference, drift, next, hr, label };
  }

  // What's coming when the rest ends — the whole point of a face you read from across the bench.
  // Pulled from `options.nextSet()` rather than tracked here: the caller owns the workout, and a
  // rest started from outside the logger simply doesn't supply one, leaving the face as it was.
  // Re-read on every update() so a set logged on the other phone doesn't leave this stale.
  function refreshNext() {
    let n = null;
    try { n = options.nextSet?.() || null; } catch { n = null; }
    const text=n?.exerciseName ? `Next: ${n.exerciseName}${n.target?` · ${n.target}`:""}${n.total>1?` · set ${n.index} of ${n.total}`:""}` : "No pending set";
    if(nextEl.textContent!==text)nextEl.textContent=text;
    if(!aod)return;
    if (!n?.exerciseName) { setChildren(aod.next); return; }
    setChildren(aod.next,
      h("b", null, n.exerciseName),
      n.target ? ` · ${n.target}` : null,
      n.total > 1 ? ` · set ${n.index} of ${n.total}` : null);
  }

  // Any touch or key anywhere restarts the idle countdown. A tap that lands on the face itself is
  // handled by the overlay's own listener; this capture-phase pass only re-arms when it isn't up.
  function onInteract() {
    if (!aod) armAod();
  }
  document.addEventListener("pointerdown", onInteract, true);
  document.addEventListener("keydown", onInteract, true);

  // Exactly one side owns the alert at a time: the page while it's visible (beep + buzz), the OS
  // while it isn't. Handing the deadline over on hide and taking it back on show keeps a
  // backgrounded rest honest without ever double-alerting.
  let alarmAt = 0; // deadline currently parked with the OS, 0 = none (avoids redundant bridge calls)
  function syncAlarm() {
    if (finished || !restAlertsEnabled()) return;
    if (document.visibilityState === "hidden") {
      if (alarmAt === endAt) return;
      alarmAt = endAt;
      local.scheduleRestOver(new Date(endAt));
    } else {
      // A cross-device FCM update may already have armed the same native alarm while this
      // WebView was waking. The visible page owns the beep, so always cancel that OS fallback;
      // if the page is hidden later, the branch above arms it again.
      alarmAt = 0;
      local.cancelRestOver();
    }
  }

  function onVisibility() {
    syncAlarm();
    if (document.visibilityState !== "visible") {
      // Screen off or another app in front: drop the face (and the dimming with it) rather than
      // leave it queued behind whatever the user is now looking at. `dismissed: false` so coming
      // back to a still-running rest earns it again.
      exitAod({ dismissed: false });
      return;
    }
    if (left() <= 0) { done({ silent: true }); return; } // it ran out while we were away
    armAod();
    paint();
  }
  document.addEventListener("visibilitychange", onVisibility);

  function done({ silent = false } = {}) {
    if (finished) return;
    finished = true;
    if (!silent) {
      beep();
      if (navigator.vibrate) navigator.vibrate([200, 80, 200]);
      notifyRestOver();
    }
    stopRest();
  }

  active = {
    el,
    tick: setInterval(tick, 500),
    onVisibility,
    onInteract,
    // Everything that outlives the DOM node: the held screen, the dimmed window, the idle timer.
    releaseScreen() {
      exitAod();
      void display.keepAwake(false);
    },
    get endAt() { return endAt; },
    update(nextEndAt, nextOptions = {}) {
      options = nextOptions;
      attachRest(options.container?.() || document.body);
      refreshNext();
      if (Math.abs(nextEndAt - endAt) < 500) {
        syncAlarm();
        return;
      }
      endAt = nextEndAt;
      total = Math.max(1, Math.ceil((endAt - Date.now()) / 1000));
      finished = false;
      syncAlarm();
      paint();
    },
  };
  paint();
  syncAlarm();
  void display.keepAwake(true);
  armAod();
  return active;
}

export function stopRest() {
  if (!active) return;
  clearInterval(active.tick);
  document.removeEventListener("visibilitychange", active.onVisibility);
  document.removeEventListener("pointerdown", active.onInteract, true);
  document.removeEventListener("keydown", active.onInteract, true);
  active.releaseScreen();
  local.cancelRestOver();
  active.el.classList.add("rt-closing");
  active.el.remove();
  active = null;
}

// PWA path: when the app is backgrounded a beep won't be heard, so fire a service-worker
// notification instead. The Android shell has no Web Notification API at all — there the OS
// alarm scheduled in syncAlarm() is what actually reaches the user.
function notifyRestOver() {
  if (document.visibilityState !== "hidden") return;
  if (!restAlertsEnabled()) return;
  if (!("Notification" in window) || Notification.permission !== "granted") return;
  navigator.serviceWorker?.ready
    .then((reg) =>
      reg.showNotification("Rest complete 💪", {
        body: "Time for your next set.",
        tag: "wt-rest",
        renotify: true,
        vibrate: [200, 80, 200],
        icon: "/icons/icon-192x192.png",
        badge: "/icons/icon-192x192.png",
      })
    )
    .catch(() => {});
}

function beep() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = "sine";
    osc.frequency.value = 880;
    gain.gain.setValueAtTime(0.001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.3, ctx.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.5);
    osc.start();
    osc.stop(ctx.currentTime + 0.5);
    setTimeout(() => ctx.close(), 800);
  } catch {}
}
